import { useEffect, useState } from "react";
import { ProgramMembership, UpdateProgramMembershipsInput } from "../../API";
import { QueryStatus } from "../../app/types";
import { MembershipPreview } from "../Users/EditUser";
import {
  fetchMembershipsByUserId,
  MembershipApiError,
  ScopedAccessRemoval,
  updateProgramMemberships
} from "./membershipAPI";

export const useMembershipsByUserId = (id: string | undefined) => {
  const [memberships, setMemberships] = useState<ProgramMembership[] | null>(
    null
  );
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getMembershipsByUserId = async (id: string) => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchMembershipsByUserId(id);
      setMemberships(results);
      setStatus("succeeded");
    } catch (error) {
      setStatus("failed");
      setError(`Error loading memberships: ${error}`);
    }
  };

  useEffect(() => {
    if (id) {
      getMembershipsByUserId(id);
    }
  }, [id]);

  return {
    memberships,
    status,
    error
  };
};

export type UpdateMembershipsOutcome = {
  success: boolean;
  errorStatus: number | null;
  /** The server's refusal code (SE-3027), when it sent one. */
  errorCode: string | null;
  errorMessage: string | null;
  /** With errorCode CONFIRM_SCOPED_ACCESS_REMOVAL: what confirming removes. */
  removals: ScopedAccessRemoval[];
  /** SE-1724: the involvement floor (earliest allowed end date) from a 400. */
  earliestEndDate?: string;
};

export const useUpdateMemberships = () => {
  const [updatedMemberships, setUpdatedMemberships] = useState<
    ProgramMembership[] | null
  >(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  // HTTP status of the last failure, so callers can map 403 to specific copy.
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  /**
   *
   * @param userId - ID of the user in question
   * @param updatedMemberships - The memberships present on the Edit User screen
   */
  // Returns the outcome synchronously (incl. the HTTP status on failure) so
  // callers can branch on it immediately — the `errorStatus` state below only
  // updates on the next render and must NOT be read right after `await`.
  const updateMemberships = async (
    userId: string,
    updatedMemberships: MembershipPreview[],
    options: { confirmScopedAccessRemoval?: boolean } = {}
  ): Promise<UpdateMembershipsOutcome> => {
    setStatus("loading");
    setError(null);
    setErrorStatus(null);
    try {
      const input: UpdateProgramMembershipsInput = {
        userId,
        programMemberships: updatedMemberships.map((um) => ({
          id: um.id,
          programId: um.programId,
          programRole: um.programRole,
          traineeType: um.traineeType,
          yearInStage: um.yearInStage,
          endDate: um.endDate,
          startDate: um.startDate
        }))
      };
      const result = await updateProgramMemberships(input, options);
      if (result?.success) {
        setStatus("succeeded");
        return { success: true, errorStatus: null, errorCode: null, errorMessage: null, removals: [] };
      }
      // updateProgramMemberships throws on failure, so this is defensive only.
      setStatus("failed");
      setError(result?.error ?? "Generic Error");
      return { success: false, errorStatus: null, errorCode: null, errorMessage: result?.error ?? null, removals: [] };
    } catch (error) {
      const apiError = error instanceof MembershipApiError ? error : null;
      const status = apiError ? apiError.status : null;
      // SE-1724: surface the involvement-floor date (from the 400) so the
      // caller can tell the Sys Admin the earliest end date they may pick.
      const earliestEndDate = apiError?.earliestEndDate;
      const errorMessage =
        error instanceof Error && error.message ? error.message : String(error);
      console.error("error updating memberships", error);
      setStatus("failed");
      setError(`Error updating memberships: ${errorMessage}`);
      setErrorStatus(status);
      return {
        success: false,
        errorStatus: status,
        earliestEndDate,
        errorCode: apiError?.code ?? null,
        errorMessage,
        removals: apiError?.removals ?? []
      };
    }
  };

  return {
    updatedMemberships,
    status,
    error,
    errorStatus,
    updateMemberships
  };
};
